import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { powersOfAttorney } from '@/api/settlements';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import RequirePermission from '@/auth/RequirePermission';
import { validateRequired } from '@/utils/validateRequired';
import { extractApiError } from '@/utils/extractApiError';
import DatePopover from '@/components/DatePopover';
import RequiredLabel from '@/components/RequiredLabel';

const PERMISSION = 'documents.powers_of_attorney.create';

interface FormState {
  organization_id: string;
  document_date: string;
  employee_id: string;
  contractor_id: string;
  contract_id: string;
  authorized_actions: string;
  valid_from: string;
  valid_until: string;
  comment: string;
}

const EMPTY: FormState = {
  organization_id: '',
  document_date: new Date().toISOString().slice(0, 16),
  employee_id: '',
  contractor_id: '',
  contract_id: '',
  authorized_actions: '',
  valid_from: new Date().toISOString().slice(0, 10),
  valid_until: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
  comment: '',
};

const STATE_LABEL: Record<string, string> = {
  ACTIVE: 'Активна', USED: 'Використана', EXPIRED: 'Прострочена', REVOKED: 'Відкликана',
};

export default function PowerOfAttorneyEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const { availableOrgs } = usePermissionsByOrg(PERMISSION);
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const docQuery = useQuery({
    queryKey: ['power-of-attorney', id],
    queryFn: () => powersOfAttorney.get(id!),
    enabled: !isNew,
  });

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_date: d.document_date.slice(0, 16),
        employee_id: d.employee_id,
        contractor_id: d.contractor_id,
        contract_id: d.contract_id ?? '',
        authorized_actions: d.authorized_actions,
        valid_from: d.valid_from,
        valid_until: d.valid_until,
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data]);

  const state = docQuery.data?.state ?? 'ACTIVE';
  const isPosted = docQuery.data?.posted ?? false;

  const create = useMutation({
    mutationFn: () => powersOfAttorney.create({
      organization_id: form.organization_id,
      document_date: new Date(form.document_date).toISOString(),
      employee_id: form.employee_id,
      contractor_id: form.contractor_id,
      contract_id: form.contract_id || null,
      authorized_actions: form.authorized_actions,
      valid_from: form.valid_from,
      valid_until: form.valid_until,
      comment: form.comment || null,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['powers-of-attorney'] });
      navigate(`/settlements/powers-of-attorney/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  // Новий: створити та провести одним кліком (create → post).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await powersOfAttorney.create({
        organization_id: form.organization_id,
        document_date: new Date(form.document_date).toISOString(),
        employee_id: form.employee_id,
        contractor_id: form.contractor_id,
        contract_id: form.contract_id || null,
        authorized_actions: form.authorized_actions,
        valid_from: form.valid_from,
        valid_until: form.valid_until,
        comment: form.comment || null,
      });
      await powersOfAttorney.post(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['powers-of-attorney'] });
      navigate(`/settlements/powers-of-attorney/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });
  // Збережений: зберегти правки та провести одним кліком (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await powersOfAttorney.update(id!, {
        document_date: new Date(form.document_date).toISOString(),
        contract_id: form.contract_id || null,
        authorized_actions: form.authorized_actions,
        valid_from: form.valid_from,
        valid_until: form.valid_until,
        comment: form.comment || null,
      });
      await powersOfAttorney.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['powers-of-attorney'] });
      qc.invalidateQueries({ queryKey: ['power-of-attorney', id] });
    },
    onError: (e) => setError(extractApiError(e)),
  });
  const revoke = useMutation({
    mutationFn: (reason: string) => powersOfAttorney.revoke(id!, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['power-of-attorney', id] }),
    onError: (e) => setError(extractApiError(e)),
  });

  function validate(): boolean {
    setError(null);
    const err = validateRequired(
      {
        organization_id: form.organization_id,
        employee_id: form.employee_id,
        contractor_id: form.contractor_id,
        authorized_actions: form.authorized_actions,
      },
      {
        organization_id: 'Юрособа', employee_id: 'Працівник',
        contractor_id: 'Контрагент', authorized_actions: 'Дозволені дії',
      },
    );
    if (err) { setError(err); return false; }
    return true;
  }

  const handleSave = () => {
    if (!validate()) return;
    if (isNew) create.mutate();
  };
  // Новий: зберегти та провести.
  const handleSaveAndPost = () => {
    if (!validate()) return;
    createAndPost.mutate();
  };
  // Збережений: зберегти правки та провести.
  const handleUpdateAndPost = () => {
    if (!validate()) return;
    updateAndPost.mutate();
  };

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова довіреність' : `Довіреність ${docQuery.data?.document_number}`}
          {!isNew && (
            <span style={{ marginLeft: '1rem', fontSize: '0.9rem', color: '#64748b' }}>
              {STATE_LABEL[state]}
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate('/settlements/powers-of-attorney')}>Назад</button>
          {isNew && (
            <RequirePermission code={PERMISSION}>
              <button className="primary" onClick={handleSave}
                      disabled={create.isPending || createAndPost.isPending}>
                Зберегти
              </button>
            </RequirePermission>
          )}
          {isNew && (
            <RequirePermission code={PERMISSION}>
              <button className="primary" onClick={handleSaveAndPost}
                      disabled={create.isPending || createAndPost.isPending}
                      title="Зберігає та проводить документ">
                ▶ Провести
              </button>
            </RequirePermission>
          )}
          {!isNew && !isPosted && state === 'ACTIVE' && (
            <button className="primary" onClick={handleUpdateAndPost}
                    disabled={updateAndPost.isPending}
                    title="Зберігає поточні зміни та проводить документ">
              ▶ Провести
            </button>
          )}
          {!isNew && state === 'ACTIVE' && (
            <button className="danger" onClick={() => {
              const r = window.prompt('Причина відкликання:');
              if (r) revoke.mutate(r);
            }}>
              Відкликати
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <RequiredLabel>Юрособа</RequiredLabel>
          {isNew ? (
            <select value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}>
              <option value="">—</option>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
              ))}
            </select>
          ) : (
            <div>{orgList.data?.items.find((o) => o.id === form.organization_id)?.name ?? form.organization_id}</div>
          )}
        </div>
        <div className="field">
          <label>Дата документа</label>
          <DatePopover withTime value={form.document_date}
            disabled={!isNew} onChange={(v) => setForm({ ...form, document_date: v })} />
        </div>
        <div className="field">
          <RequiredLabel>Працівник (UUID)</RequiredLabel>
          <input value={form.employee_id} disabled={!isNew}
            onChange={(e) => setForm({ ...form, employee_id: e.target.value })} />
        </div>
        <div className="field">
          <RequiredLabel>Контрагент (UUID)</RequiredLabel>
          <input value={form.contractor_id} disabled={!isNew}
            onChange={(e) => setForm({ ...form, contractor_id: e.target.value })} />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <RequiredLabel>Дозволені дії</RequiredLabel>
          <textarea value={form.authorized_actions} disabled={!isNew}
            onChange={(e) => setForm({ ...form, authorized_actions: e.target.value })}
            rows={2} />
        </div>
        <div className="field">
          <label>Дійсна з</label>
          <DatePopover value={form.valid_from} disabled={!isNew}
            onChange={(v) => setForm({ ...form, valid_from: v })} />
        </div>
        <div className="field">
          <label>по</label>
          <DatePopover value={form.valid_until} disabled={!isNew}
            onChange={(v) => setForm({ ...form, valid_until: v })} />
        </div>
      </div>

      {state === 'REVOKED' && docQuery.data?.revocation_reason && (
        <div style={{ marginTop: '0.5rem', color: '#b91c1c' }}>
          <strong>Відкликано:</strong> {docQuery.data.revocation_reason}
        </div>
      )}

      {error && <div className="error" style={{ marginTop: '1rem', color: '#b91c1c' }}>{error}</div>}
    </div>
  );
}
